import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { Plus, CalendarDays, ListChecks, Settings2, Sun, RefreshCw, Loader2 } from 'lucide-react';
import { pedidosCalService } from '../services/api';
import { DEF_PARAMS, addBD, coDay, iso, plan } from './calendario/calendarLogic';
import { KpiStrip } from './calendario/KpiStrip';
import { HoyTab } from './calendario/HoyTab';
import { CalendarTab } from './calendario/CalendarTab';
import { PedidosTab } from './calendario/PedidosTab';
import { ParametrosTab } from './calendario/ParametrosTab';
import { NuevoPedidoDialog } from './calendario/NuevoPedidoDialog';

const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

function useAlerts(PL, today) {
  return useMemo(() => {
    const nb = iso(addBD(new Date(), 1)), alerts = [], risk = [], tasks = [];
    PL.forEach(({ o, pl }) => {
      (o.tareas || []).forEach((t) => { if (!t.hecha && t.fecha <= today) tasks.push({ o, pl, t }); });
      const r = pl.rows.find((x) => !x.dn);
      if (!r) return;
      const st = iso(r.st), en = iso(r.en);
      const k = en < today ? 'late' : st <= today ? 'now' : st === nb ? 'next' : null;
      if (k) alerts.push({ o, pl, r, k });
      if (pl.margin < 0) risk.push({ o, pl });
    });
    tasks.sort((a, b) => a.t.fecha.localeCompare(b.t.fecha));
    return { alerts, risk, tasks };
  }, [PL, today]);
}

export default function CalendarioBosko() {
  const [orders, setOrders] = useState([]);
  const [params, setParams] = useState(DEF_PARAMS);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('hoy');
  const [filter, setFilter] = useState('all');
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const today = coDay();

  const ordersRef = useRef([]);
  const pending = useRef(0);
  const setAll = (next) => { ordersRef.current = next; setOrders(next); };

  const load = useCallback(async (silent) => {
    try {
      const data = await pedidosCalService.getAll();
      if (silent && pending.current > 0) return;
      ordersRef.current = data.orders || [];
      setOrders(ordersRef.current);
      if (data.params) setParams(data.params);
    } catch (e) {
      if (!silent) toast.error('No se pudieron cargar los pedidos');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(true); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  const PL = useMemo(() => orders.map((o) => ({ o, pl: plan(o, params, today) })), [orders, params, today]);
  const { alerts, risk, tasks } = useAlerts(PL, today);
  const counts = useMemo(() => PL.reduce((c, { pl }) => ({ ...c, [pl.status]: (c[pl.status] || 0) + 1 }), { all: PL.length }), [PL]);

  const persist = async (o) => {
    pending.current += 1;
    try { await pedidosCalService.save(o); } catch (e) { toast.error('Error guardando. Recargando datos...'); pending.current -= 1; load(); return; }
    pending.current -= 1;
  };
  const update = (id, patch) => {
    const o = { ...ordersRef.current.find((x) => x.id === id), ...patch };
    setAll(ordersRef.current.map((x) => (x.id === id ? o : x)));
    persist(o);
  };
  const toggle = (id, i, v) => {
    const done = { ...(ordersRef.current.find((x) => x.id === id).done || {}) };
    if (v) done[i] = today; else delete done[i];
    update(id, { done });
    if (v) toast.success('Paso marcado como listo');
  };
  const create = async (f) => {
    const o = { id: newId(), ...f, done: {}, createdAt: new Date().toISOString() };
    setAll([...ordersRef.current, o]);
    setOpenNew(false);
    setTab('ped');
    setFilter('all');
    await persist(o);
    toast.success(`Pedido de ${o.cliente} creado`);
  };
  const remove = async (id) => {
    setAll(ordersRef.current.filter((x) => x.id !== id));
    try { await pedidosCalService.remove(id); toast.success('Pedido eliminado'); } catch (e) { toast.error('Error eliminando'); load(true); }
  };
  const saveEdit = (f) => {
    let patch = f;
    if (f.tipo !== editing.tipo) {
      const oldS = params[editing.tipo]?.s || [], newS = params[f.tipo]?.s || [];
      const done = {};
      Object.entries(editing.done || {}).forEach(([i, d]) => {
        const j = newS.findIndex((s) => s[0] === oldS[i]?.[0]);
        if (j >= 0) done[j] = d;
      });
      patch = { ...f, done };
    }
    update(editing.id, patch);
    setEditing(null);
    toast.success('Pedido actualizado');
  };
  const doneTask = (id, taskId) => {
    const o = ordersRef.current.find((x) => x.id === id);
    update(id, { tareas: (o.tareas || []).map((t) => (t.id === taskId ? { ...t, hecha: true } : t)) });
    toast.success('Tarea marcada como hecha');
  };
  const saveParams = async (P) => {
    setParams(P);
    try { await pedidosCalService.saveParams(P); toast.success('Parámetros guardados'); } catch (e) { toast.error('Error guardando parámetros'); }
  };

  const TABS = [
    { k: 'hoy', label: 'Hoy', Icon: Sun, badge: alerts.length + tasks.length },
    { k: 'cal', label: 'Calendario', Icon: CalendarDays },
    { k: 'ped', label: 'Pedidos', Icon: ListChecks },
    { k: 'par', label: 'Parámetros', Icon: Settings2 },
  ];
  const tabIds = { hoy: 'tab-hoy', cal: 'tab-calendario', ped: 'tab-pedidos', par: 'tab-parametros' };

  return (
    <div className="min-h-screen bg-slate-50/60 p-4 md:p-8" data-testid="calendario-pedidos" style={{ fontFamily: "'Plus Jakarta Sans', Inter, system-ui, sans-serif" }}>
      <Toaster position="top-right" richColors closeButton />
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600">BOSKO · Producción</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Calendario de pedidos</h1>
            <p className="text-sm text-slate-500 mt-1">Días hábiles de Colombia · compartido entre todos los equipos</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="btn-refrescar" onClick={() => load()} className="p-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 transition-colors" title="Actualizar"><RefreshCw size={18} /></button>
            <button data-testid="btn-nuevo-pedido" onClick={() => setOpenNew(true)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm shadow-indigo-200 transition-colors"><Plus size={18} /> Nuevo pedido</button>
          </div>
        </header>

        <KpiStrip counts={counts} active={tab === 'ped' ? filter : null} onSelect={(k) => { setFilter(k); setTab('ped'); }} />

        <nav className="inline-flex p-1 bg-slate-100 rounded-xl border border-slate-200 mb-6 gap-1 w-full sm:w-auto overflow-x-auto">
          {TABS.map(({ k, label, Icon, badge }) => (
            <button key={k} data-testid={tabIds[k]} onClick={() => setTab(k)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${tab === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}>
              <Icon size={16} /> {label}
              {badge ? <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{badge}</span> : null}
            </button>
          ))}
        </nav>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 p-10 justify-center" data-testid="calendario-loading"><Loader2 className="animate-spin" size={18} /> Cargando pedidos...</div>
        ) : (
          <div key={tab} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            {tab === 'hoy' && <HoyTab alerts={alerts} risk={risk} tasks={tasks} onDoneTask={doneTask} hasOrders={orders.length > 0} onMark={toggle} today={today} />}
            {tab === 'cal' && <CalendarTab PL={PL} today={today} />}
            {tab === 'ped' && <PedidosTab PL={PL} filter={filter} setFilter={setFilter} today={today} onToggle={toggle} onUpdate={update} onDelete={remove} onEdit={setEditing} />}
            {tab === 'par' && <ParametrosTab key={JSON.stringify(params)} params={params} onSave={saveParams} />}
          </div>
        )}
      </div>
      <NuevoPedidoDialog open={openNew} onOpenChange={setOpenNew} params={params} onCreate={create} />
      {editing ? <NuevoPedidoDialog key={editing.id} open onOpenChange={(v) => !v && setEditing(null)} params={params} initial={editing} onCreate={saveEdit} /> : null}
    </div>
  );
}
